// Spec A §9 — Types, DTOs en Zod-schemas voor contract- en salarismutaties.
//
// Mirrort de Prisma-enums `ContractMutationReason` en `SalaryMutationReason`
// uit `packages/db/prisma/schema.prisma` (§2.1, §2.2).
//
// `MutationStatus` is een afgeleide UI-status, niet opgeslagen in de database
// — bepaald door API-laag op basis van `deletedAt` + `effectiveFrom` +
// `effectiveTo` (zie spec §4.1).
//
// DTOs gebruiken ISO-datetime-strings; conversie van/naar `Date` gebeurt in
// de API-laag. Zo blijven types client-vriendelijk (serializable over HTTP).

import { z } from 'zod'

// ── Enums ──────────────────────────────────────────────────────────────────

export const CONTRACT_MUTATION_REASONS = [
  'NIEUW_CONTRACT',
  'VERLENGING',
  'URENWIJZIGING',
  'FUNCTIEWIJZIGING',
  'CAO_AANPASSING',
  'CORRECTIE',
  'EINDE_PROEFTIJD',
  'OMZETTING',
  'BIJTELLING_WIJZIGING',
  'VERLOFOPBOUW_WIJZIGING',
] as const
export type ContractMutationReason = (typeof CONTRACT_MUTATION_REASONS)[number]

export const SALARY_MUTATION_REASONS = [
  'NIEUW_CONTRACT',
  'CAO_VERHOGING',
  'PROMOTIE',
  'PERIODIEK',
  'FUNCTIEWIJZIGING',
  'CORRECTIE',
] as const
export type SalaryMutationReason = (typeof SALARY_MUTATION_REASONS)[number]

/**
 * Afgeleide UI-status per mutatie-regel (spec §4.1). Niet in de database.
 *
 * - `GEANNULEERD` — `deletedAt !== null`
 * - `GEPLAND` — `effectiveFrom > vandaag`
 * - `ACTIEF` — `effectiveFrom ≤ vandaag` EN (`effectiveTo IS NULL` OF `effectiveTo > vandaag`)
 * - `HISTORISCH` — anders (verlopen, opgevolgd)
 */
export type MutationStatus = 'HISTORISCH' | 'ACTIEF' | 'GEPLAND' | 'GEANNULEERD'

// ── Labels (NL voor UI) ────────────────────────────────────────────────────

export const CONTRACT_MUTATION_REASON_LABELS: Record<ContractMutationReason, string> = {
  NIEUW_CONTRACT:   'Nieuw contract',
  VERLENGING:       'Verlenging',
  URENWIJZIGING:    'Urenwijziging',
  FUNCTIEWIJZIGING: 'Functiewijziging',
  CAO_AANPASSING:   'CAO-aanpassing',
  CORRECTIE:        'Correctie',
  EINDE_PROEFTIJD:  'Einde proeftijd',
  OMZETTING:        'Omzetting naar onbepaalde tijd',
  BIJTELLING_WIJZIGING: 'Bijtelling-wijziging',
  // batch-68 (FO-344 §3.4) - dezelfde woorden als het mutatieverzoek.
  VERLOFOPBOUW_WIJZIGING: 'Wijziging werktijd en sparen (ATV/TVT)',
}

export const SALARY_MUTATION_REASON_LABELS: Record<SalaryMutationReason, string> = {
  NIEUW_CONTRACT:   'Nieuw contract',
  CAO_VERHOGING:    'CAO-verhoging',
  PROMOTIE:         'Promotie',
  PERIODIEK:        'Periodieke verhoging',
  FUNCTIEWIJZIGING: 'Functiewijziging',
  CORRECTIE:        'Correctie',
}

// ── DTOs (response shapes) ─────────────────────────────────────────────────

export interface ContractMutationDTO {
  id: string
  employeeId: string
  status: MutationStatus
  effectiveFrom: string // ISO-datetime
  effectiveTo: string | null
  contractType: string
  contractDurationMonths: number | null
  contractEndDate: string | null
  contractHoursPerWeek: number
  atvHoursPerWeek: number
  bijtelling: boolean | null
  bijtellingPercentage: number | null
  // Spec 40 (P3/B2) — handmatige override + server-berekende snapshot-bedragen en
  // -koppelingen op de mutatie. De read-DTO geeft ze terug zodat de sheet de
  // override-toggle/-reden hydrateert en het afgeleide maandbedrag read-only toont.
  bijtellingHandmatig: boolean
  bijtellingHandmatigReden: string | null
  bijtellingMaandbedrag: number | null
  bijtellingJaarbedrag: number | null
  bijtellingVoertuigId: string | null
  bijtellingRegelingId: string | null
  // FO-361 (batch-41) — eigen bijdrage privégebruik per maand (drukt de
  // belaste bijtelling in de netto-indicatie; nooit onder nul).
  bijtellingEigenBijdrageMaand: number | null
  werkUrenPerWeek: number | null
  atvEnabled: boolean
  tvtWerkurenEnabled: boolean
  tvtReisurenEnabled: boolean
  // FO-122 — opbouwpercentage per TVT-soort (50 of 100). 100 = volledige opbouw
  // (default/bestaand gedrag). Bij TVT-uit genormaliseerd naar 100.
  tvtWerkOpbouwPercentage: number
  tvtReisOpbouwPercentage: number
  caoBeleidId: string | null
  verlofBijzonderheden: string | null
  // Parttime-/vast rooster per weekdag (spec parttime-rooster-verlof-verzuim).
  werktVastRooster: boolean | null
  urenMaandag: number | null
  urenDinsdag: number | null
  urenWoensdag: number | null
  urenDonderdag: number | null
  urenVrijdag: number | null
  urenZaterdag: number | null
  urenZondag: number | null
  reason: ContractMutationReason
  reasonLabel: string
  note: string | null
  createdByName: string
  createdAt: string
}

export interface SalaryMutationDTO {
  id: string
  employeeId: string
  status: MutationStatus
  effectiveFrom: string
  effectiveTo: string | null
  grossMonthlySalary: number
  caoSchaal: string | null
  caoTrede: number | null
  parttimeFactor: number | null
  hourlyRate: number | null
  reason: SalaryMutationReason
  reasonLabel: string
  note: string | null
  createdByName: string
  createdAt: string
}

/**
 * Waarschuwing dat het BESCHIKBARE verlofsaldo van een emmer door deze
 * contractwijziging onder 0 zakt. Verschilt bewust van `verlofImpact` (dat het
 * heel-jaars RECHT toont): hier rekenen we het reeds-opgenomen + gereserveerde
 * saldo mee, zodat een mid-jaar verlaging (klassiek: ATV uit terwijl al ATV is
 * opgenomen) zichtbaar wordt vóór goedkeuren. Puur informatief - blokkeert niets.
 */
export interface SaldoWaarschuwing {
  emmerType: 'WETTELIJK' | 'BOVENWETTELIJK' | 'ATV'
  /** Mensvriendelijk label, bv. "ATV" / "Wettelijk" / "Bovenwettelijk". */
  label: string
  /** Beschikbaar saldo vóór de correctie (opgebouwd - opgenomen - gereserveerd). */
  huidigBeschikbaar: number
  /** De (negatieve) pro-rata correctie op het opbouwtempo voor de rest van het jaar. */
  correctie: number
  /** Resulterend beschikbaar saldo na de correctie (< 0). */
  naCorrectie: number
}

/**
 * Dry-run preview voor een geplande contractmutatie (spec §4.4).
 * Wordt door `POST /employees/:id/contract-mutations/preview` teruggegeven.
 */
export interface ContractMutationPreviewDTO {
  verlofImpact: {
    emmer: string
    huidigSaldoPerJaar: number
    nieuwSaldoPerJaar: number
    verschil: number
  }[]
  payrollImpact: {
    contractUrenOud: number
    contractUrenNieuw: number
    brutoMaandloonEffect: string
  }
  /** Emmers waarvan het beschikbare saldo door deze wijziging negatief wordt. */
  saldoWaarschuwingen: SaldoWaarschuwing[]
  warnings: string[]
}

/**
 * Compleet dry-run preview vóór een supersede-correctie met terugwerkende kracht
 * (`POST /employees/:id/contract-mutations/:mutationId/corrigeer/preview`).
 * Toont álles wat de correctie raakt; schrijft niets.
 */
export interface CorrectiePreviewDTO {
  /** De actieve mutatie die vervangen wordt. */
  supersededMutationId: string
  effectiveFrom: string // ISO
  effectiveTo: string | null
  /** Gewijzigde velden (oud → nieuw), enkel wat daadwerkelijk verandert. */
  veldWijzigingen: { veld: string; oud: string; nieuw: string }[]
  /** Velden die ongewijzigd worden overgenomen (niet door het form gewist). */
  behouden: { veld: string; waarde: string }[]
  /** Weekrooster oud → nieuw (ma..zo); null als beide geen vast rooster hebben. */
  roosterWijziging: {
    oud: { werktVastRooster: boolean; dagen: (number | null)[] }
    nieuw: { werktVastRooster: boolean; dagen: (number | null)[] }
    totaalOud: number
    totaalNieuw: number
  } | null
  /** Verlof-opbouw per emmer: heel-jaars saldo huidig → nieuw + verschil. */
  verlofImpact: { emmer: string; huidigPerJaar: number; nieuwPerJaar: number; verschil: number }[]
  /** Feestdag-naar-rato compensatie voor het lopende jaar, met de berekening
   *  (recht − reeds genoten = compensatie) zodat de invoerder ziet hoe het bedrag
   *  is opgebouwd. `feestdagen` = de doordeweekse feestdagen die op de vrije dag
   *  vallen (deze loopt de medewerker mis). */
  feestdagCompensatie: {
    jaar: number
    uren: number
    contracturen: number
    doordeweekseFeestdagen: number
    recht: number
    reedsGenoten: number
    feestdagen: { datum: string; naam: string }[]
    status: 'NIEUW' | 'AL_TOEGEKEND' | 'GEEN_VAST_ROOSTER' | 'GEEN_COMPENSATIE'
  }
  /** Ouderschaps-rest-recht wordt herberekend (alleen bij urenwijziging + bestaande emmers). */
  ouderschapHerberekend: boolean
  payroll: {
    contractUrenOud: number
    contractUrenNieuw: number
    brutoMaandloonEffect: string
    bijtellingGewijzigd: boolean
  }
  kostprijs: { fteFactorOud: number; fteFactorNieuw: number; herberekend: boolean; voltijdsUrenPerWeek: number }
  /** Read-side doorwerking (weekstaat/verzuim/planning), tekstueel. */
  doorwerking: string[]
  /** Audit-regels die bij uitvoering worden vastgelegd. */
  auditRegels: string[]
  /** Emmers waarvan het beschikbare saldo door deze correctie negatief wordt. */
  saldoWaarschuwingen: SaldoWaarschuwing[]
  warnings: string[]
}

/**
 * Preview voor een CAO-ronde (bulk salary mutation, spec §4.3).
 * Per medewerker huidige + nieuwe salaris + delta; plus totaal.
 */
export interface BulkSalaryPreviewDTO {
  medewerkers: {
    employeeId: string
    naam: string
    huidigSalaris: number
    nieuwSalaris: number
    delta: number
  }[]
  totaalDelta: number
}

// ── Zod-schemas (request bodies) ───────────────────────────────────────────

/**
 * Bijtellingspercentage als heel getal 0-100. Een waarde in (0,1) is vrijwel
 * zeker een breuk (bv. 0,22 i.p.v. 22) en wordt geweigerd met een duidelijke
 * hint, zodat de "0,22%"-bug niet via een mutatie kan terugkeren. Zie spec 40 §2.3.
 */
export const bijtellingPercentageSchema = z
  .number()
  .min(0)
  .max(100)
  .refine((n) => !(n > 0 && n < 1), {
    message: 'Percentage lijkt een breuk (bijv. 0,22). Bedoelde je 22? Vul het als heel getal in.',
  })
  .nullable()
  .optional()

/**
 * FO-122 — TVT-opbouwpercentage (alleen 50 of 100; geen vrije invoer zoals 25/75/
 * 12,5 — voorkomt vreemde payroll-/saldo-afwijkingen). Optioneel i.p.v. een Zod-
 * `.default(100)`: de default-100 + het normaliseren-bij-TVT-uit gebeurt op de
 * service-/handler-laag (`?? 100`), zodat een weggelaten veld een opgeslagen 50%
 * nooit per ongeluk reset (de PATCH-route deelt dezelfde guard).
 */
export const tvtOpbouwPercentageSchema = z
  .number()
  .refine((v) => v === 50 || v === 100, {
    message: 'TVT-opbouwpercentage moet 50 of 100 zijn',
  })
  .optional()

/**
 * Base-schema ZONDER `.default()`s. Bewust gescheiden van het create-schema:
 * Zod's `.default()` vult ook onder `.partial()` afwezige keys in, waardoor een
 * partial-PATCH die alleen `note` stuurt stil `atvEnabled/tvtWerkurenEnabled/
 * atvHoursPerWeek` naar false/0 zou resetten (en daarmee een verlof-recalc zou
 * triggeren). PATCH-schemas horen daarom van DIT schema af te leiden
 * (`contractMutationBaseSchema.partial()`), nooit van het create-schema.
 */
export const contractMutationBaseSchema = z.object({
  effectiveFrom:          z.string().min(1),
  contractType:           z.enum(['BEPAALDE_TIJD', 'ONBEPAALDE_TIJD', 'OPROEP', 'STAGE', 'PAYROLL']),
  contractDurationMonths: z.number().int().positive().nullable().optional(),
  contractEndDate:        z.string().nullable().optional(),
  contractHoursPerWeek:   z.number().positive(),
  atvHoursPerWeek:        z.number().min(0),
  bijtelling:             z.boolean().nullable().optional(),
  bijtellingPercentage:   bijtellingPercentageSchema,
  // Spec 40 (P3) - handmatige override op de auto-als-bron-default. Bij `true`
  // is de operator-ingevulde `bijtellingPercentage` leidend en laten de
  // herbereken-/snapshot-triggers de mutatie ongemoeid (de override wint tot HR
  // 'm opheft). Geaudit + zichtbaar als badge "handmatig".
  bijtellingHandmatig:    z.boolean(),
  bijtellingHandmatigReden: z.string().max(500).nullable().optional(),
  // FO-361 (batch-41) — eigen bijdrage privégebruik per maand (0 - 2.500;
  // zelfde grens als de simulator-override, spec 63 §6).
  bijtellingEigenBijdrageMaand: z.number().min(0).max(2_500).nullable().optional(),
  werkUrenPerWeek:        z.number().positive().nullable().optional(),
  atvEnabled:             z.boolean(),
  tvtWerkurenEnabled:     z.boolean(),
  tvtReisurenEnabled:     z.boolean(),
  // FO-122 — opbouwpercentage per TVT-soort (zie tvtOpbouwPercentageSchema).
  tvtWerkOpbouwPercentage: tvtOpbouwPercentageSchema,
  tvtReisOpbouwPercentage: tvtOpbouwPercentageSchema,
  caoBeleidId:            z.string().nullable().optional(),
  verlofBijzonderheden:   z.string().nullable().optional(),
  // Parttime-/vast rooster per weekdag (spec parttime-rooster-verlof-verzuim).
  // Uren per dag in [0, 24]; werktVastRooster=true maakt het rooster leidend.
  werktVastRooster:       z.boolean().nullable().optional(),
  urenMaandag:            z.number().min(0).max(24).nullable().optional(),
  urenDinsdag:            z.number().min(0).max(24).nullable().optional(),
  urenWoensdag:           z.number().min(0).max(24).nullable().optional(),
  urenDonderdag:          z.number().min(0).max(24).nullable().optional(),
  urenVrijdag:            z.number().min(0).max(24).nullable().optional(),
  urenZaterdag:           z.number().min(0).max(24).nullable().optional(),
  urenZondag:             z.number().min(0).max(24).nullable().optional(),
  reason:                 z.enum(CONTRACT_MUTATION_REASONS),
  note:                   z.string().max(2000).nullable().optional(),
})

/** Create/volledige-snapshot-schema: base + defaults voor weglaatbare vlaggen. */
export const createContractMutationSchema = contractMutationBaseSchema.extend({
  atvHoursPerWeek:     z.number().min(0).default(0),
  bijtellingHandmatig: z.boolean().default(false),
  atvEnabled:          z.boolean().default(false),
  tvtWerkurenEnabled:  z.boolean().default(false),
  tvtReisurenEnabled:  z.boolean().default(false),
})
export type CreateContractMutationInput = z.infer<typeof createContractMutationSchema>

/**
 * PATCH-schema voor een GEPLANDE mutatie: alle velden optioneel, ZONDER
 * defaults (afgeleid van het default-loze base-schema) en zonder
 * `effectiveFrom` (ingangsdatum verschuiven = annuleren + opnieuw aanmaken).
 * Een weggelaten veld blijft zo écht `undefined` — een PATCH met alleen
 * `note` kan nooit ATV/TVT-vlaggen of -percentages resetten.
 */
export const updateContractMutationSchema = contractMutationBaseSchema
  .partial()
  .omit({ effectiveFrom: true })
export type UpdateContractMutationInput = z.infer<typeof updateContractMutationSchema>

export const createSalaryMutationSchema = z.object({
  effectiveFrom:      z.string().min(1),
  grossMonthlySalary: z.number().positive(),
  caoSchaal:          z.string().max(20).nullable().optional(),
  caoTrede:           z.number().int().min(0).nullable().optional(),
  parttimeFactor:     z.number().min(0).max(1).nullable().optional(),
  reason:             z.enum(SALARY_MUTATION_REASONS),
  note:               z.string().max(2000).nullable().optional(),
})

export const bulkSalaryMutationSchema = z.object({
  effectiveFrom:   z.string().min(1),
  employeeIds:     z.array(z.string()).min(1).max(500),
  method:          z.enum(['PERCENTAGE', 'ABSOLUTE']),
  percentage:      z.number().min(0).max(50).optional(),
  absoluteAmount:  z.number().min(0).optional(),
  incrementTrede:  z.boolean().default(false),
  reason:          z.literal('CAO_VERHOGING'),
  note:            z.string().max(2000).nullable().optional(),
})
